import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Button } from "../ui/button";
import { ArrowLeft, LayoutGrid, ChevronRight } from "lucide-react";

/**
 * EmailAssetDetail — DASH-EMAIL-011b, cut over in DASH-EMAIL-012A to the
 * real Asset Version detail (GET
 * creative-analytics/assets/{family}/{asset_id}/versions/{version}).
 *
 * Two things are gone rather than fabricated (DASH-EMAIL-012A Frozen
 * Decisions 15/16):
 *  - contentPreview: no asset family has a canonical preview field exposed
 *    by the real read API today.
 *  - The CTA Fingerprint panel: CTA Fingerprint DOES exist in persistence
 *    (EMAIL-CATALOG-003), but EMAIL-CATALOG-007A's read projection does not
 *    yet expose it (AssetVersionDetailResponse has no such field) — this is
 *    an API-exposure gap, not a data-existence gap. Follow-up: expose the
 *    already-persisted CTA Fingerprint through the Email Marketing read
 *    API. Not implemented here — no backend changes in this checkpoint.
 *
 * The per-metric Unique Clicks/Click Rate/ROI tiles are replaced by one
 * real `channel2_metrics` pass-through — the real response has a single
 * Channel-2 availability object, not a per-outcome metric breakdown.
 *
 * Usage is now TWO real lists (Creative Versions AND Broadcasts using this
 * asset version) — the real API returns both; the mock only ever exposed
 * Creative Version usage. Creative Versions usage shows creative_id (not a
 * resolved Creative name — the real usage list carries no name field).
 */
export default function EmailAssetDetail({ detail, onBack, onNavigateToCreativeVersion }) {
  if (!detail) return null;

  const isUnavailable = detail.channel2_metrics?.availability === "UNAVAILABLE";

  return (
    <div className="space-y-4" data-testid="email-asset-detail">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-1.5 text-slate-300 hover:text-slate-100"
        onClick={onBack}
        data-testid="email-asset-detail-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to Assets
      </Button>

      <Card className="glass border-white/10" data-testid="email-asset-detail-identity">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <LayoutGrid className="h-5 w-5 text-indigo-400" />
            <span className="text-lg font-semibold text-slate-100">
              {detail.asset_id} <span className="text-slate-400">v{detail.version}</span>
            </span>
            <span className="text-sm text-slate-400">{detail.family}</span>
            <span className="text-sm text-slate-400">{detail.status}</span>
          </div>
          <p className="mt-2 text-sm text-slate-300">{detail.name}</p>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-300 sm:grid-cols-3">
            <span>
              Creative Versions using it: <span className="text-slate-100">{detail.creative_versions?.length || 0}</span>
            </span>
            <span>
              Broadcasts: <span className="text-slate-100">{detail.broadcasts?.length || 0}</span>
            </span>
            <span data-testid="email-asset-detail-channel2">
              Channel-2 Outcomes:{" "}
              <span className="text-slate-100">
                {isUnavailable ? "—" : Number(detail.channel2_metrics?.value || 0).toLocaleString()}
              </span>
            </span>
          </div>
        </CardContent>
      </Card>

      <Card className="glass border-white/10" data-testid="email-asset-detail-usage-table">
        <CardHeader>
          <CardTitle className="text-lg text-slate-100">Creative Versions Using This Asset Version</CardTitle>
        </CardHeader>
        <CardContent>
          {!detail.creative_versions || detail.creative_versions.length === 0 ? (
            <p className="text-sm text-slate-500">No Creative Versions reference this asset version.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-slate-400">Creative</TableHead>
                  <TableHead className="text-slate-400">Version</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.creative_versions.map((u) => (
                  <TableRow
                    key={`${u.creative_id}-${u.version}`}
                    className="cursor-pointer border-white/10 hover:bg-white/5"
                    data-testid={`email-asset-detail-usage-row-${u.creative_id}-${u.version}`}
                    onClick={() => onNavigateToCreativeVersion?.(u.creative_id, u.version)}
                  >
                    <TableCell className="text-sm text-slate-100">{u.creative_id}</TableCell>
                    <TableCell className="text-sm text-slate-300">v{u.version}</TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="glass border-white/10" data-testid="email-asset-detail-broadcasts-table">
        <CardHeader>
          <CardTitle className="text-lg text-slate-100">Broadcasts Using This Asset Version</CardTitle>
        </CardHeader>
        <CardContent>
          {!detail.broadcasts || detail.broadcasts.length === 0 ? (
            <p className="text-sm text-slate-500">No Broadcasts reference this asset version.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-slate-400">Broadcast</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.broadcasts.map((b) => (
                  <TableRow key={b.broadcast_id} className="border-white/10 hover:bg-transparent" data-testid={`email-asset-detail-broadcast-row-${b.broadcast_id}`}>
                    <TableCell className="text-sm text-slate-100">{b.broadcast_id}</TableCell>
                    <TableCell className="text-sm text-slate-300">{b.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
